// TextBoxElement.jsx - Text element with real-time tag value display
import React from 'react';

export default function TextBoxElement({
  element,
  tagValues,
  darkMode = false,
  isSelected = false,
  onClick,
  onDoubleClick,
  cursor
}) {
  const {
    x = 0,
    y = 0,
    width = 120,
    height = 40,
    rotation = 0,
    textContent = 'Text',
    tagName,
    showTagValue = false,
    valueFormat = '{value}',
    decimalPlaces,
    thousandsSep = false,
    prefix = '',
    suffix = '',
    fontSize = 14,
    fontFamily = 'Inter, system-ui, sans-serif',
    fontWeight = 'normal',
    textAlign = 'center',
    color,
    backgroundColor,
    borderColor,
    borderWidth = 0,
    borderRadius = 4,
    padding = 8
  } = element;

  // Get current tag value
  const tagValue = tagName && tagValues ? tagValues[tagName] : null;

  // Format a numeric tag value with decimal/separator/prefix/suffix options
  const formatTagValue = (raw) => {
    const num = parseFloat(raw);
    let formatted;
    if (!isNaN(num)) {
      const dp = decimalPlaces !== undefined && decimalPlaces !== '' ? parseInt(decimalPlaces) : null;
      if (thousandsSep) {
        formatted = num.toLocaleString('en-US', {
          minimumFractionDigits: dp ?? 0,
          maximumFractionDigits: dp ?? 20
        });
      } else {
        formatted = dp !== null ? num.toFixed(dp) : String(num);
      }
    } else {
      formatted = String(raw);
    }
    return `${prefix}${formatted}${suffix}`;
  };

  // Format display text
  const getDisplayText = () => {
    if (showTagValue && tagValue !== undefined && tagValue !== null) {
      const formattedValue = formatTagValue(tagValue);
      return valueFormat
        .replace('{value}', formattedValue)
        .replace('{label}', element.label || tagName || '')
        .replace('{tag}', tagName || '');
    }
    return textContent;
  };

  const displayText = getDisplayText();

  // Default colors based on dark mode
  const defaultTextColor = darkMode ? '#e5e7eb' : '#111827';
  const defaultBgColor = darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)';
  const defaultBorderColor = darkMode ? '#475569' : '#d1d5db';

  return (
    <g
      transform={`translate(${x}, ${y}) rotate(${rotation}, ${width / 2}, ${height / 2})`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      style={{ cursor: cursor || 'default' }}
    >
      {/* Background */}
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill={backgroundColor || defaultBgColor}
        stroke={borderWidth > 0 ? (borderColor || defaultBorderColor) : 'none'}
        strokeWidth={borderWidth}
        rx={borderRadius}
        ry={borderRadius}
      />

      {/* Text content using foreignObject for proper text rendering */}
      <foreignObject x={0} y={0} width={width} height={height} style={{ pointerEvents: 'none' }}>
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: textAlign === 'center' ? 'center' :
                           textAlign === 'right' ? 'flex-end' : 'flex-start',
            padding: `${padding}px`,
            boxSizing: 'border-box',
            fontSize: `${fontSize}px`,
            fontFamily: fontFamily,
            fontWeight: fontWeight,
            color: color || defaultTextColor,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            userSelect: 'none',
            pointerEvents: 'none'
          }}
        >
          {displayText}
        </div>
      </foreignObject>

      {/* Selection indicator */}
      {isSelected && (
        <rect
          x={-2}
          y={-2}
          width={width + 4}
          height={height + 4}
          fill="none"
          stroke={darkMode ? '#60a5fa' : '#3b82f6'}
          strokeWidth={2}
          strokeDasharray="4 2"
          rx={borderRadius + 2}
          pointerEvents="none"
        />
      )}
    </g>
  );
}

// Default text box configuration
export const DEFAULT_TEXTBOX = {
  type: 'textbox',
  width: 120,
  height: 40,
  textContent: 'Label',
  showTagValue: false,
  valueFormat: '{value}',
  fontSize: 14,
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: 'normal',
  textAlign: 'center',
  borderRadius: 4,
  borderWidth: 1,
  padding: 8
};
